import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import apiClient from "../api/apiClient";
import InfiniteScroll from "../components/InfiniteScroll";
import LoadingSpinner from "../components/LoadingSpinner";
import WarningModal from "../modals/WarningModal";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { PurchasePriceCell } from "../components/PurchaseCouponMark";

const RETURN_WARNING_TEXT =
  "You will lose access to this mockup forever. You will be refunded. Are you sure you want to request a return?";

const RETURN_WINDOW_DAYS = 14;

const purchaseTableStyle = {
  width: "100%",
  minWidth: "720px",
  borderCollapse: "separate",
  borderSpacing: "0 10px",
  tableLayout: "fixed",
};

const purchaseHeaderCellStyle = {
  border: "none",
  padding: "8px 16px",
  fontSize: "13px",
  fontWeight: 600,
  color: "#6c757d",
  letterSpacing: "0.02em",
  verticalAlign: "bottom",
};

const purchaseBodyCellStyle = {
  border: "none",
  padding: "14px 16px",
  verticalAlign: "middle",
  backgroundColor: "#fff",
};

const purchaseRowShadowStyle = {
  filter: "drop-shadow(0 2px 8px rgba(99, 99, 99, 0.2))",
};

const parsePurchaseDate = (createdAt) => {
  if (!createdAt) return null;

  if (/^\d{1,2}:\d{2} [AP]M$/i.test(createdAt)) {
    const today = new Date().toISOString().split("T")[0];
    const date = new Date(`${today} ${createdAt}`);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(createdAt);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatPurchaseDateTime = (createdAt) => {
  const date = parsePurchaseDate(createdAt);
  if (!date) return createdAt || "—";

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const getPurchaseThumbnailSrc = (productDetails) => {
  if (!productDetails) return null;

  if (productDetails.use_main_file_as_thumbnail && productDetails.compressed_file) {
    return productDetails.compressed_file;
  }

  const thumbnail =
    productDetails.thumbnails?.[0]?.image ||
    productDetails.bundle_thumbnails?.[0]?.image;

  return thumbnail || productDetails.compressed_file || null;
};

const isWithinReturnWindow = (createdAt) => {
  const purchaseDate = parsePurchaseDate(createdAt);
  if (!purchaseDate) return false;

  const now = new Date();
  const windowMs = RETURN_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  return now - purchaseDate <= windowMs;
};

const canRequestReturn = (sale) =>
  Boolean(sale) && !sale?.is_refunded && isWithinReturnWindow(sale?.created_at);

/** Route to storefront listing (mockup vs bundle) from sale product_details. */
const getPurchaseListingPath = (productDetails) => {
  const id = productDetails?.id;
  if (id == null || id === "") return null;
  const isBundle =
    Array.isArray(productDetails?.bundle_thumbnails) &&
    productDetails.bundle_thumbnails.length > 0;
  return isBundle ? `/bundle?id=${id}` : `/mockup?id=${id}`;
};

function PurchaseThumbnail({ productDetails, title }) {
  const [hasError, setHasError] = useState(false);
  const src = getPurchaseThumbnailSrc(productDetails);
  const showPlaceholder = !src || hasError;

  if (showPlaceholder) {
    return (
      <div
        className="rounded-3 d-flex align-items-center justify-content-center"
        style={{
          width: "80px",
          height: "80px",
          backgroundColor: "#E9ECEF",
          color: "#ADB5BD",
        }}
        aria-hidden="true"
      >
        <ImageNotSupportedOutlinedIcon fontSize="small" />
      </div>
    );
  }

  return (
    <img
      className="rounded-3"
      src={src}
      alt={title ? `${title} thumbnail` : "Purchase thumbnail"}
      style={{
        objectFit: "cover",
        objectPosition: "center",
        width: "80px",
        height: "80px",
        display: "block",
      }}
      onError={() => setHasError(true)}
    />
  );
}

function PurchaseRowMenuItems({ sale, onClose, onRequestReturn }) {
  const listingPath = getPurchaseListingPath(sale?.product_details);
  const showReturn = canRequestReturn(sale);
  return (
    <>
      {listingPath ? (
        <MenuItem component={Link} to={listingPath} onClick={onClose}>
          Go to listing
        </MenuItem>
      ) : (
        <MenuItem disabled>Go to listing</MenuItem>
      )}
      {showReturn ? (
        <MenuItem
          onClick={() => {
            onClose();
            onRequestReturn(sale);
          }}
          sx={{ fontSize: "0.875rem" }}
        >
          Request return
        </MenuItem>
      ) : null}
    </>
  );
}

const Purchases = () => {
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedSaleForReturn, setSelectedSaleForReturn] = useState(null);
  const [returnSubmitting, setReturnSubmitting] = useState(false);
  const [actionsMenu, setActionsMenu] = useState(null);

  const fetchdata = useMemo(() => {
    return async (page) => {
      const params = new URLSearchParams();
      params.append("data", "p");
      params.append("page", page);
      params.append("page_size", "8");
      return await apiClient.get(`/sales/?${params.toString()}`);
    };
  }, []);

  const { data: listingdata, loading, hasMore, loadMore, refetch } = useInfiniteScroll(
    fetchdata,
    [],
    8
  );

  const openReturnModal = (sale) => {
    setSelectedSaleForReturn(sale);
    setShowReturnModal(true);
  };

  const closeReturnModal = () => {
    setShowReturnModal(false);
    setSelectedSaleForReturn(null);
  };

  const handleConfirmReturn = async () => {
    if (!selectedSaleForReturn?.id) return;
    setReturnSubmitting(true);
    try {
      const result = await apiClient.post(
        `/sales/${selectedSaleForReturn.id}/request-return/`
      );
      if (result?.ok) {
        toast.success("Return requested successfully. You will be refunded.");
        closeReturnModal();
        refetch();
      } else {
        const message =
          result?.data?.error || "Failed to request return. Please try again.";
        toast.error(message);
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to request return.");
    } finally {
      setReturnSubmitting(false);
    }
  };

  const openActionsMenu = (event, sale) => {
    setActionsMenu({ anchorEl: event.currentTarget, sale });
  };

  const closeActionsMenu = () => {
    setActionsMenu(null);
  };

  return (
    <>
      {loading && listingdata?.length === 0 ? (
        <div className="d-flex justify-content-center mt-5">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          <section>
            <div className="container">
              <div className="row">
                <div className="col-md-5 mb-2">
                  <h2 className=" mb-3" style={{ color: "#000000e6" }}>
                    Purchases
                  </h2>
                </div>
              </div>
            </div>
          </section>
          <div className="container-md px-2">
            {listingdata?.length > 0 ? (
              <div style={{ overflowX: "auto" }}>
                <table style={purchaseTableStyle}>
                  <colgroup>
                    <col style={{ width: "96px" }} />
                    <col />
                    <col style={{ width: "18%" }} />
                    <col style={{ width: "140px" }} />
                    <col style={{ width: "180px" }} />
                    <col style={{ width: "48px" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        aria-hidden="true"
                        style={purchaseHeaderCellStyle}
                      />
                      <th
                        scope="col"
                        style={{ ...purchaseHeaderCellStyle, textAlign: "left" }}
                      >
                        Title
                      </th>
                      <th
                        scope="col"
                        style={{ ...purchaseHeaderCellStyle, textAlign: "left" }}
                      >
                        Seller
                      </th>
                      <th
                        scope="col"
                        style={{ ...purchaseHeaderCellStyle, textAlign: "right" }}
                      >
                        Price
                      </th>
                      <th
                        scope="col"
                        style={{ ...purchaseHeaderCellStyle, textAlign: "right" }}
                      >
                        Purchase Date
                      </th>
                      <th
                        scope="col"
                        aria-hidden="true"
                        style={purchaseHeaderCellStyle}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {listingdata.map((element, index) => {
                      const title = element?.product_details?.title;
                      const seller = element?.product_details?.user_name;
                      const price =
                        element?.price ?? element?.product_details?.price;

                      return (
                        <tr
                          key={element?.id || index}
                          style={purchaseRowShadowStyle}
                        >
                          <td
                            style={{
                              ...purchaseBodyCellStyle,
                              borderRadius: "16px 0 0 16px",
                              paddingLeft: "20px",
                              width: "96px",
                            }}
                          >
                            <PurchaseThumbnail
                              productDetails={element?.product_details}
                              title={title}
                            />
                          </td>
                          <td style={purchaseBodyCellStyle}>
                            <p
                              className="mb-0 fw-semibold text-truncate"
                              style={{ fontSize: "14px" }}
                              title={title}
                            >
                              {title || "—"}
                            </p>
                          </td>
                          <td style={purchaseBodyCellStyle}>
                            <p
                              className="mb-0 text-truncate"
                              style={{ fontSize: "13px" }}
                              title={seller}
                            >
                              {seller || "—"}
                            </p>
                          </td>
                          <td
                            style={{
                              ...purchaseBodyCellStyle,
                              textAlign: "right",
                            }}
                          >
                            <PurchasePriceCell
                              price={price}
                              coupon={element?.coupon}
                              isRefunded={element?.is_refunded}
                            />
                          </td>
                          <td
                            style={{
                              ...purchaseBodyCellStyle,
                              textAlign: "right",
                            }}
                          >
                            <p className="purchase-date">
                              {formatPurchaseDateTime(element?.created_at)}
                            </p>
                          </td>
                          <td
                            style={{
                              ...purchaseBodyCellStyle,
                              borderRadius: "0 16px 16px 0",
                              paddingRight: "12px",
                              width: "48px",
                            }}
                          >
                            <IconButton
                              type="button"
                              size="small"
                              aria-label="Purchase actions"
                              onClick={(e) => openActionsMenu(e, element)}
                              sx={{
                                color: "text.secondary",
                                "&:hover": { backgroundColor: "action.hover" },
                              }}
                            >
                              <MoreHorizIcon fontSize="small" />
                            </IconButton>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <InfiniteScroll
                  onLoadMore={loadMore}
                  hasMore={hasMore}
                  loading={loading}
                />
              </div>
            ) : null}
          </div>
          <Menu
            anchorEl={actionsMenu?.anchorEl}
            open={Boolean(actionsMenu?.anchorEl)}
            onClose={closeActionsMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              elevation: 3,
              sx: { minWidth: 180 },
            }}
          >
            {actionsMenu?.sale ? (
              <PurchaseRowMenuItems
                sale={actionsMenu.sale}
                onClose={closeActionsMenu}
                onRequestReturn={openReturnModal}
              />
            ) : null}
          </Menu>
          <WarningModal
            showModal={showReturnModal}
            modalClose={closeReturnModal}
            setConfirmClose={handleConfirmReturn}
            text={RETURN_WARNING_TEXT}
            confirmDisabled={returnSubmitting}
            confirmLabel={returnSubmitting ? "Processing..." : undefined}
          />
        </>
      )}
    </>
  );
};

export default Purchases;
